import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, Square, Send, X, Loader2, Play } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function VoiceRecorderButton({ onSend, disabled, testid = "voice-record-btn" }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startRef = useRef(0);

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => () => {
    stopTimer();
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  }, []);

  const start = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => stream.getTracks().forEach((t) => t.stop());
      recRef.current = rec;
      rec.start();
      startRef.current = Date.now();
      setSecs(0);
      timerRef.current = setInterval(() => setSecs(Math.floor((Date.now() - startRef.current) / 1000)), 500);
      setState("recording");
    } catch {
      setErr("Microphone access denied");
    }
  };

  const finish = async (sendIt) => {
    stopTimer();
    const rec = recRef.current;
    if (!rec || rec.state === "inactive") { setState("idle"); return; }
    const duration = (Date.now() - startRef.current) / 1000;
    rec.stop();
    if (!sendIt) { setState("idle"); chunksRef.current = []; return; }
    setState("uploading");
    // give ondataavailable a tick to flush
    setTimeout(async () => {
      const mime = rec.mimeType || "audio/webm";
      const ext = mime.includes("mp4") ? "mp4" : "webm";
      const blob = new Blob(chunksRef.current, { type: mime });
      chunksRef.current = [];
      try {
        await onSend(blob, ext, duration);
      } catch {
        setErr("Couldn't send — try again");
      }
      setState("idle");
    }, 150);
  };

  if (state === "recording") {
    return (
      <div data-testid="voice-recording-ui" className="flex items-center gap-2 bg-[#875F45]/10 border border-[#875F45]/40 rounded-full px-3 py-2">
        <span className="w-2 h-2 rounded-full bg-[#875F45] animate-pulse" />
        <span className="text-xs font-semibold text-[#875F45] tabular-nums" data-testid="voice-timer">{fmtSecs(secs)}</span>
        <button type="button" onClick={() => finish(false)} data-testid="voice-cancel-btn" aria-label="Cancel recording"
          className="w-7 h-7 rounded-full bg-[#EDEDEE] text-[#3F5043] flex items-center justify-center hover:bg-[#BCB3A6]/40">
          <X size={13} />
        </button>
        <button type="button" onClick={() => finish(true)} data-testid="voice-send-btn" aria-label="Send voice message"
          className="w-7 h-7 rounded-full bg-[#3F5043] text-[#F8F7F3] flex items-center justify-center hover:bg-[#875F45]">
          <Send size={13} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={start}
        disabled={disabled || state === "uploading"}
        data-testid={testid}
        aria-label="Record voice message"
        className="w-11 h-11 rounded-full bg-[#EDEDEE] text-[#3F5043] flex items-center justify-center hover:bg-[#BCB3A6]/40 transition-colors disabled:opacity-50 flex-shrink-0"
      >
        {state === "uploading" ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
      </button>
      {err && <span className="text-[10px] text-[#875F45]" data-testid="voice-error">{err}</span>}
    </div>
  );
}

export function VoiceMessagePlayer({ messageId, duration, authHeader, mine, testid, audioUrl, requestParams }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const load = async () => {
    if (url || loading) return;
    setLoading(true);
    setErr(false);
    try {
      const res = await axios.get(audioUrl || `${API}/dm/voice/${messageId}/audio`, {
        headers: authHeader ? authHeader() : {},
        params: requestParams || {},
        responseType: "blob",
        withCredentials: true,
      });
      setUrl(URL.createObjectURL(res.data));
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  return (
    <div data-testid={testid || `voice-player-${messageId}`} className={`flex items-center gap-2 px-3 py-2 rounded-2xl ${
      mine ? "bg-[#3F5043] text-[#F8F7F3] rounded-br-sm" : "bg-[#875F45]/15 text-[#3F5043] border border-[#875F45]/30 rounded-bl-sm"
    }`}>
      {url ? (
        <audio controls src={url} className="h-8 max-w-[220px]" data-testid={`voice-audio-${messageId}`} />
      ) : (
        <button type="button" onClick={load} disabled={loading} data-testid={`voice-play-${messageId}`}
          className={`flex items-center gap-2 text-xs font-medium ${mine ? "text-[#F8F7F3]" : "text-[#3F5043]"}`}>
          <span className={`w-8 h-8 rounded-full flex items-center justify-center ${mine ? "bg-[#F8F7F3]/15" : "bg-[#3F5043]/10"}`}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          </span>
          {err ? "Couldn't load audio" : `Voice message${duration ? ` · ${fmtSecs(duration)}` : ""}`}
        </button>
      )}
    </div>
  );
}
